import {UntilDestroy, untilDestroyed} from '@ngneat/until-destroy';
import {Component, Inject, OnInit} from '@angular/core';
import {WORKBENCH_VIEW_CONFIG, WorkbenchViewConfig} from '../../../shared/types';
import {DeviceId, ExpeditionId, ProjectId} from '@webmate-web-core/shared/types/ids';
import {faInfoCircle, faStopCircle} from '@fortawesome/pro-solid-svg-icons';
import {BrowserSessionInfo} from '@webmate-web-core/shared/types/browsersession';
import {BrowserSessionService} from '@webmate-web-core/shared/services/browsersession.service';
import {DeviceService} from '@webmate-web-core/shared/services/device/device.service';
import {WorkbenchConsoleService} from '../../../shared/services/workbench-console.service';
import {faDesktop} from '@fortawesome/pro-light-svg-icons';
import {WorkbenchViewComponent} from '../../../shared/components/viewcontainers/workbench-view.component';
import {WorkbenchLayoutManager} from '../../../shared/services/workbench-layout-manager.service';
import {WorkbenchContextService} from '../../../shared/services/workbench-context.service';
import {
    DeployDeviceDialogComponent,
    DeployDeviceDialogToolConfig
} from '@webmate-web-core/shared/tools/device-deployment/deploy-device-dialog.component';
import {ModalToolContainerService} from '@webmate-web-core/shared/tools/modaltoolcontainer/modaltoolcontainer.service';
import {finalize, timeout} from 'rxjs/operators';
import {WorkbenchExpeditionService} from '../../../shared/services/workbench-expedition.service';


@UntilDestroy()
@Component({
    templateUrl: './browsersessions-view.component.html',
    styleUrls: ['./browsersessions-view.component.less'],
})
export class BrowserSessionsViewComponent extends WorkbenchViewComponent implements OnInit {

    projectId: ProjectId;

    private sessions: BrowserSessionInfo[] = [];

    /**
     * Ids of devices seen to be headless. Only ever added to (within the view's lifetime), so a session
     * on a headless device keeps its headless status even after the (ephemeral) device is torn down on
     * stop — otherwise the platform label would briefly flash back in before the session row disappears.
     */
    private readonly headlessDeviceIds = new Set<DeviceId>();

    activeExpedition: ExpeditionId | undefined;

    /** True while the active session is locked by a running analysis — session-changing controls are disabled. */
    locked = false;

    /** Always restrict the list to the current user's sessions (the user-facing toggle was removed). */
    readonly onlyForCurrentUser = true;

    /**
     * True from clicking "Deploy Browser" until its console actually opens (or the attempt fails).
     * Unlike a plain device deployment — where a Device object exists almost immediately — a headless
     * browser session first needs its backing device deployed and only then starts the session, so this
     * spans notably longer and needs its own local loading state rather than reusing device-deploy UI.
     */
    startingHeadlessBrowser = false;

    constructor(@Inject(WORKBENCH_VIEW_CONFIG) public config: WorkbenchViewConfig<any>,
                private browserSessionService: BrowserSessionService,
                private deviceService: DeviceService,
                private workbenchExpeditionService: WorkbenchExpeditionService,
                layoutManager: WorkbenchLayoutManager,
                private workbenchConsoleService: WorkbenchConsoleService,
                private readonly workbenchContextService: WorkbenchContextService,
                private toolService: ModalToolContainerService) {
        super(layoutManager, config);
    }


    ngOnInit() {
        const workbenchContext = this.workbenchContextService.getCurrentWorkbenchContext()!;
        this.projectId = workbenchContext.project;

        this.workbenchExpeditionService.observeActiveExpeditionsInProject$().pipe(untilDestroyed(this)).subscribe(sessions => {
            this.sessions = sessions;
        });

        this.deviceService.getDevicesForProject$(this.projectId).pipe(untilDestroyed(this)).subscribe(devices => {
            devices.filter(device => device.isHeadlessDevice()).forEach(device => this.headlessDeviceIds.add(device.id));
        });

        this.workbenchContextService.subscribeWorkbenchContext$().subscribe(context => {
            this.activeExpedition = context.expeditionContext?.activeExpedition?.id;
            this.locked = !!context.expeditionContext?.activeExpeditionLocked;
        });
    }

    getSessionsToDisplay(): BrowserSessionInfo[] {
        if (this.onlyForCurrentUser) {
            const currentUserId = this.workbenchContextService.getUserId();
            return this.sessions.filter(session => session.user === currentUserId);
        } else {
            return this.sessions;
        }
    }

    /** Whether the given session runs on a headless device (platform is not meaningful for those). */
    isHeadlessSession(session: BrowserSessionInfo): boolean {
        return !!session.optDeviceId && this.headlessDeviceIds.has(session.optDeviceId);
    }

    makeExpeditionActive(expedition: BrowserSessionInfo) {
        if (this.locked) {
            return;
        }
        this.workbenchContextService.setContextActiveExpedition(expedition);
    }

    accessConsole(expedition: BrowserSessionInfo) {
        if (this.locked) {
            return;
        }
        this.workbenchConsoleService.openConsoleForExpedition(expedition.id).subscribe(_ => {
            this.workbenchContextService.setContextActiveExpedition(expedition);
        });
    }

    stopSession(id: ExpeditionId): void {
        if (this.locked) {
            return;
        }
        this.browserSessionService.stopExpedition$(id).subscribe(_ => {
            const currentContext = this.workbenchContextService.getCurrentWorkbenchContext();
            if (currentContext?.expeditionContext?.activeExpedition && currentContext.expeditionContext?.activeExpedition.id === id){
                this.workbenchContextService.setContextActiveExpedition(undefined);
            }
        });
    }

    startHeadlessBrowser() {
        if (this.startingHeadlessBrowser) {
            return;
        }
        this.startingHeadlessBrowser = true;

        // The shared lifecycle waits for the ready BrowserSession snapshot before storing it in the
        // Workbench context, so startup metadata such as the QualitySense FactSession is available.
        this.workbenchExpeditionService.startHeadlessSession$('https://testfabrik.com').pipe(
            // The device backing the session has to be deployed first, so this budget is generous
            // compared to browser-session starts on an already-existing device.
            timeout(120000),
            finalize(() => this.startingHeadlessBrowser = false)
        ).subscribe({
            error: error => console.error('Error starting headless browser session', error)
        });
    }

    openBrowserDeploymentTool() {
        if (this.locked) {
            return;
        }
        this.toolService.openTool(DeployDeviceDialogComponent, new DeployDeviceDialogToolConfig({
            projectId: this.projectId,
            platformFamily: "desktop",
            selectedGroupingVariant: "browser",
            browserSessionDefault: true
        }))
    }
    protected readonly faStopCircle = faStopCircle;
    protected readonly faDesktop = faDesktop;
    protected readonly faInfoCircle = faInfoCircle;
}
